import {
    ArrayInput,
    AutocompleteArrayInput,
    AutocompleteInput,
    FormDataConsumer,
    ReferenceArrayInput,
    ReferenceInput,
    required,
    SimpleFormIterator,
    useRecordContext,
    useSimpleFormIteratorItem,
    useTranslate,
    useSourceContext,
} from 'react-admin';
import { Box } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { AddButton, RemoveButton } from '@/components/global/buttons/CustomSimpleFormButtons.jsx';
import { contractorFilterToQuery, collectionPointFilterToQuery } from '@/helpers/filters/filterToQuery.js';
import { roleIsPosOwner } from '@/constants/Roles.js';
import { Palette } from '@/themes/themeOverrides/Palette';

const CustomAddButton = () => {
    const translate = useTranslate();
    return (
        <AddButton
            label={translate('common.addContractor')}
            sx={{
                fontWeight: '400',
                borderRadius: '12px',
            }}
        />
    );
};
const CustomRemoveButton = () => {
    const translate = useTranslate();
    const { index } = useSimpleFormIteratorItem();
    if (index === 0) {
        return null;
    }
    return <RemoveButton label={translate('common.remove')} />;
};

const CollectionPointArrayInput = ({ contractorId, contractorSelected, collectionPointSelected, source }) => {
    const translate = useTranslate();
    const { clearErrors } = useFormContext();

    useEffect(() => {
        if (!contractorSelected) {
            clearErrors(source);
        }
    }, [contractorSelected, clearErrors, source]);

    return (
        <ReferenceArrayInput
            source={source}
            reference='collection-point'
            perPage={100}
            filter={{ contractorId: contractorId }}
            queryOptions={{ enabled: contractorSelected }}
        >
            <AutocompleteArrayInput
                className='custom-select-array-input'
                label={translate('common.collectionPoint')}
                filterToQuery={collectionPointFilterToQuery}
                validate={contractorSelected ? required(translate('common.validation.required')) : undefined}
                disabled={!contractorSelected}
                disablePortal={true}
                sx={{
                    flex: 1,
                    '&.MuiAutocomplete-root.ra-input': {
                        minWidth: '320px',
                        maxWidth: '320px',
                    },
                    '& .MuiInputBase-root': {
                        width: '100%',
                        gap: '2px',
                        '& .MuiAutocomplete-endAdornment': {
                            right: '14px',
                        },
                    },
                    '& .MuiButtonBase-root': {
                        color: Palette.black,
                        fontWeight: 400,
                        borderRadius: '8px',
                        margin: '0',
                        '& .MuiChip-label': {
                            padding: '0 8px',
                        },
                    },
                    '& .MuiButtonBase-root.MuiIconButton-root': {
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg id='icon'%3E%3Cpath id='Vector 16' d='M16 16L11 11' stroke='%231A1A1A' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Ccircle id='Ellipse 7' cx='9' cy='9' r='5' fill='white' stroke='%231A1A1A' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/g%3E%3C/svg%3E%0A")`,
                    },
                    '& .Mui-focused': {
                        '& .MuiButtonBase-root.MuiAutocomplete-popupIndicator.MuiAutocomplete-popupIndicator': {
                            transform: 'rotate(0)',
                        },
                    },
                    ...(collectionPointSelected && {
                        '& .MuiChip-root': {
                            fontSize: '24px',
                        },
                        '& .MuiFormLabel-root.MuiInputLabel-root': {
                            transform: 'translate(14px, -11px) scale(1)',
                        },
                        '& .MuiInputBase-root.MuiOutlinedInput-root': {
                            padding: '10px 40px 10px 16px !important',
                        },
                    }),
                }}
            />
        </ReferenceArrayInput>
    );
};

CollectionPointArrayInput.propTypes = {
    contractorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    contractorSelected: PropTypes.bool.isRequired,
    collectionPointSelected: PropTypes.bool.isRequired,
    source: PropTypes.string.isRequired,
};

const UsersContractorsListManager = ({ ...props }) => {
    const record = useRecordContext();
    const sourceContext = useSourceContext();
    const translate = useTranslate();
    const { resetField, setValue, getValues } = useFormContext();
    const initializedRef = useRef(false);

    const initialContractors = useMemo(() => {
        if (!record) return [{}];
        let contractors = [];
        if (record.contractorCollectionPointIds && Object.keys(record.contractorCollectionPointIds).length > 0) {
            contractors = Object.entries(record.contractorCollectionPointIds).map(([contractorId, collectionPointIds]) => ({
                id: contractorId,
                collectionPointIds: Array.isArray(collectionPointIds) ? collectionPointIds : [collectionPointIds],
            }));
        } else if (record.contractorIds) {
            contractors = record.contractorIds.map(value => ({ id: value }));
        }
        return contractors.length > 0 ? contractors : [{}];
    }, [record]);

    useEffect(() => {
        if (record?.id && !initializedRef.current) {
            const currentContractors = getValues('contractors');
            const isEmpty = !currentContractors || currentContractors.length === 0 || (currentContractors.length === 1 && !currentContractors[0]?.id);
            if (isEmpty && initialContractors.length > 0 && initialContractors[0]?.id) {
                setValue('contractors', initialContractors, { shouldDirty: false, shouldValidate: false });
                initializedRef.current = true;
            }
        }
    }, [getValues, initialContractors, record?.id, setValue]);

    return (
        <ArrayInput source='contractors' label='' {...props} defaultValue={initialContractors} sx={{ '& > .MuiFormHelperText-root': { display: 'none' } }}>
            <SimpleFormIterator
                inline
                disableClear
                disableRemove
                disableReordering
                sx={{
                    '& .RaSimpleFormIterator-form': {
                        display: 'flex',
                        alignItems: 'flex-start',
                        flexWrap: 'nowrap',
                        justifyContent: 'space-between',
                        columnGap: '12px',
                        marginBottom: '-2px',
                        width: '100%',
                    },
                    '& .RaSimpleFormIterator-line': {
                        width: '100%',
                        borderBottom: 'none',
                        marginBottom: '3px',
                    },
                    '& .MuiAutocomplete-root': {
                        minWidth: 'unset',
                    },
                }}
                addButton={<CustomAddButton />}
            >
                <FormDataConsumer>
                    {() => {
                        return (
                            <Box className='autocomplete-parent'>
                                <ReferenceInput source={sourceContext.getSource('id')} reference='contractor' perPage={100}>
                                    <AutocompleteInput
                                        className='custom-select-array-input'
                                        label={translate('common.contractor')}
                                        filterToQuery={contractorFilterToQuery}
                                        validate={required(translate('common.validation.required'))}
                                        onChange={() => {
                                            resetField(sourceContext.getSource('collectionPointIds'));
                                            setValue(sourceContext.getSource('collectionPointIds'), []);
                                        }}
                                        disablePortal={true}
                                    />
                                </ReferenceInput>
                            </Box>
                        );
                    }}
                </FormDataConsumer>
                {roleIsPosOwner(props.role) ? (
                    <FormDataConsumer>
                        {({ scopedFormData }) => {
                            const contractorSelected = Boolean(scopedFormData?.id);
                            const collectionPointSelected = Array.isArray(scopedFormData?.collectionPointIds) && scopedFormData.collectionPointIds.length > 0;
                            return (
                                <Box className='autocomplete-parent'>
                                    <CollectionPointArrayInput
                                        source={sourceContext.getSource('collectionPointIds')}
                                        contractorId={scopedFormData?.id}
                                        contractorSelected={contractorSelected}
                                        collectionPointSelected={collectionPointSelected}
                                    />
                                </Box>
                            );
                        }}
                    </FormDataConsumer>
                ) : null}
                <CustomRemoveButton />
            </SimpleFormIterator>
        </ArrayInput>
    );
};

export default UsersContractorsListManager;
